import Checkbox from '@/components/elements/forms/Checkbox';
import Header from '@/components/elements/Header/Header';
import { classnames } from '@/helpers';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import Markdown from 'markdown-to-jsx';
import { Fragment, MouseEvent, useState } from 'react';
import * as sanitizeHtml from 'sanitize-html';
import Modal from '../../modals/redesign/Modal/Modal';

type BookingTermsProps = {
  terms: string | React.ReactNode;
  termsAccepted: boolean;
  error?: boolean;
  onChange: (checked: boolean) => void;
};

const baseTranslationKey = 'components.modules.checkout.BookingTerms';

const BookingTerms = ({ terms, termsAccepted, error, onChange }: BookingTermsProps) => {
  const { isMobileView } = useMobileView();
  const [showTerms, setShowTerms] = useState(false);

  const markdown = sanitizeHtml(terms ?? '', {
    allowedTags: [],
    allowedAttributes: {},
  });

  const handleOnShowTermsClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowTerms(true);
    bookingFlowTrackingSession.terms('initiated', 'T&C_merchant');
  };

  const handleOnCloseTermsClick = () => {
    setShowTerms(false);
    bookingFlowTrackingSession.terms('closed', 'T&C_merchant');
  };

  return (
    <Fragment>
      <div className="px-lg py-md gap-md flex flex-col">
        <Header label={_t(`${baseTranslationKey}.title`)} size="lg" />

        <Checkbox
          className={classnames(
            'p-xs pr-sm pl-md gap-md flex cursor-pointer flex-row items-center justify-between rounded-sm border',
            error && 'border-border_negative bg-bg_negative',
            !error && 'hover:bg-bg_secondary_bold active:bg-bg_secondary_pressed',
            termsAccepted
              ? 'border-border_selected bg-bg_secondary'
              : !error && 'border-border_bold bg-bg_secondary_neutral',
          )}
          data-cy="bookingTermsButton"
          checked={termsAccepted}
          onChange={onChange}
          label={
            <span className="text-l flex-">
              {_t(`${baseTranslationKey}.description`)}
              <button
                onClick={handleOnShowTermsClick}
                className="text-fg_link inline underline focus:outline-none focus:ring-0 focus-visible:ring-2">
                {_t(`${baseTranslationKey}.terms`)}
              </button>
            </span>
          }
        />
        {error && <p className="text-fg_negative text-s">{_t(`${baseTranslationKey}.error`)}</p>}
      </div>

      <Modal isOpen={showTerms}>
        <Modal.Content floating={!isMobileView}>
          <Modal.Header title={_t(`${baseTranslationKey}.modal.title`)} onClose={handleOnCloseTermsClick} />
          <div className="px-xxl py-md md:p-0">
            <Markdown options={{ wrapper: TermsWrapper }}>{markdown}</Markdown>
          </div>
        </Modal.Content>
      </Modal>
    </Fragment>
  );
};

const TermsWrapper = ({ children }) => {
  return <div className="space-y-sm relative flex flex-col">{children}</div>;
};

export default BookingTerms;
